import React, { useEffect, useId, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiDownload, FiExternalLink, FiFileText } from 'react-icons/fi';
import { useI18n } from './i18n';
import './InvoiceHistory.css';

// Stripe charges use two decimal places except these zero-decimal currencies.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf',
  'vnd', 'vuv', 'xaf', 'xof', 'xpf',
]);

export function formatInvoiceAmount(total, currency, language) {
  const code = currency.toLowerCase();
  return new Intl.NumberFormat(language, { style: 'currency', currency: code }).format(
    total / (ZERO_DECIMAL_CURRENCIES.has(code) ? 1 : 100),
  );
}

export default function InvoiceHistory({ apiFetch }) {
  const { t, language } = useI18n();
  const panelId = useId();
  const requestRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  useEffect(() => () => requestRef.current?.abort(), []);

  async function loadPage(cursor = null) {
    if (requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError(false);
    try {
      const query = cursor ? `?starting_after=${encodeURIComponent(cursor)}` : '';
      const data = await apiFetch(`/billing/invoices${query}`, { signal: controller.signal, cache: 'no-store' });
      if (controller.signal.aborted) return;
      setInvoices(previous => cursor ? [...previous, ...data.invoices] : data.invoices);
      setNextCursor(data.has_more ? data.next_cursor : null);
      setLoaded(true);
    } catch (failure) {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }

  function toggleHistory() {
    setExpanded(!expanded);
    if (!expanded && !loaded) loadPage();
  }

  return (
    <div className="form-group invoice-history">
      <button type="button" className="btn secondary-btn invoice-history-toggle"
        aria-expanded={expanded} aria-controls={panelId} onClick={toggleHistory}>
        <FiFileText aria-hidden="true" />
        <span>{t(expanded ? 'profile.hideInvoices' : 'profile.viewInvoices')}</span>
        {expanded ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
      </button>
      {expanded && (
        <section id={panelId} className="invoice-history-panel" aria-label={t('profile.invoiceHistory')} aria-busy={loading}>
          {invoices.length > 0 && (
            <ul className="invoice-history-list">
              {invoices.map(invoice => {
                const date = new Date(invoice.created * 1000);
                const label = invoice.number || t('profile.invoice');
                return (
                  <li key={invoice.id} className="invoice-history-item">
                    <div className="invoice-history-summary">
                      <time dateTime={date.toISOString()}>{date.toLocaleDateString(language, {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}</time>
                      <strong>{formatInvoiceAmount(invoice.total, invoice.currency, language)}</strong>
                    </div>
                    <div className="invoice-history-details">
                      <span className="invoice-history-number">{label}</span>
                      <span className={`invoice-history-status invoice-history-status--${invoice.status}`}>
                        {t(`profile.invoiceStatus.${invoice.status}`)}
                      </span>
                    </div>
                    {invoice.description && <p className="invoice-history-description">{invoice.description}</p>}
                    <div className="invoice-history-links">
                      {invoice.hosted_invoice_url && (
                        <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                          aria-label={t('profile.openInvoiceLabel', { number: label })}>
                          <FiExternalLink aria-hidden="true" /> {t('profile.openInvoice')}
                        </a>
                      )}
                      {invoice.invoice_pdf && (
                        <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer"
                          aria-label={t('profile.downloadInvoiceLabel', { number: label })}>
                          <FiDownload aria-hidden="true" /> {t('profile.downloadInvoice')}
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {loading && <p className="invoice-history-message" role="status">{t('profile.loadingInvoices')}</p>}
          {error && (
            <div className="invoice-history-error">
              <p role="alert">{t('profile.invoicesFailed')}</p>
              <button type="button" className="btn secondary-btn" onClick={() => loadPage(nextCursor)}>
                {t('profile.retryInvoices')}
              </button>
            </div>
          )}
          {loaded && !loading && !error && invoices.length === 0 && (
            <p className="invoice-history-message">{t('profile.noInvoices')}</p>
          )}
          {nextCursor && !error && (
            <button type="button" className="btn secondary-btn" disabled={loading} onClick={() => loadPage(nextCursor)}>
              {t('profile.olderInvoices')}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
